// ==============================|| TYPES - LEGAL & CONSENT ||============================== //
// Wire shapes from PRIVACY_CONSENT_API.md. Consent copy is fixed English and is never
// machine-translated — render it inside a `data-no-translate` subtree.

export type ConsentType = 'PRIVACY_TERMS' | 'SMS';
export type LegalDocumentType = 'PRIVACY_POLICY' | 'TERMS';
export type LegalSlug = 'privacy-policy' | 'terms';
export type ConsentPlatform = 'APP' | 'DASHBOARD' | 'WEBLINK';
export type ConsentPlatformKey = 'app' | 'dashboard' | 'weblink';
export type ConsentSource = 'LOGIN' | 'ADMIN';

export const LEGAL_SLUGS: Record<LegalDocumentType, LegalSlug> = {
  PRIVACY_POLICY: 'privacy-policy',
  TERMS: 'terms'
};

export const LEGAL_DOCUMENT_TYPES: LegalDocumentType[] = ['PRIVACY_POLICY', 'TERMS'];

export const LEGAL_DOCUMENT_LABELS: Record<LegalDocumentType, string> = {
  PRIVACY_POLICY: 'Privacy Policy',
  TERMS: 'Terms & Conditions'
};

export const CONSENT_TYPE_LABELS: Record<ConsentType, string> = {
  PRIVACY_TERMS: 'Privacy Policy & Terms',
  SMS: 'SMS notifications'
};

export const CONSENT_PLATFORM_LABELS: Record<ConsentPlatform, string> = {
  APP: 'App',
  DASHBOARD: 'Dashboard',
  WEBLINK: 'Weblink'
};

export const CONSENT_COPY = {
  privacyTermsPrefix: 'I agree to the ',
  sms: '(Optional) I agree to receive SMS from Quikstep about my assigned shipments and transactions. Message frequency varies. Msg & data rates may apply. Reply HELP for help, STOP to opt out.',
  helper: 'Please accept to continue',
  consentRequired: 'Please accept the items below to continue.'
};

export interface ConsentToggle {
  enabled: boolean;
  platforms: Record<ConsentPlatformKey, boolean>;
}

export interface LegalConsentSettings {
  privacyTerms: ConsentToggle;
  sms: ConsentToggle;
}

export interface LegalDocumentMeta {
  uid: string;
  type: LegalDocumentType;
  version: number;
  title: string;
  effectiveDate: string;
  // Only on a publish response.
  createdAt?: string;
  createdBy?: { uid?: string; name?: string } | string | null;
}

export interface LegalDocument extends LegalDocumentMeta {
  content: string;
}

export type LegalDocumentsMeta = Record<LegalDocumentType, LegalDocumentMeta | null>;

// `data.legal` on `utility/config`. Everything is optional: a missing or malformed piece
// means "show nothing" and the login 428 is the backstop.
export interface LegalConfig {
  consent?: {
    privacyTerms?: Partial<ConsentToggle> | null;
    sms?: Partial<ConsentToggle> | null;
  } | null;
  documents?: Partial<LegalDocumentsMeta> | null;
}

// `payload.consents` on `account/login` — only the boxes that were shown AND ticked.
export interface LoginConsents {
  privacyTerms?: { accepted: true; privacyPolicyUid?: string; termsUid?: string };
  sms?: { accepted: true };
}

export interface LegalDocumentSlot {
  current: LegalDocumentMeta | null;
  // Newest first.
  versions: LegalDocumentMeta[];
}

export interface ClientLegalOverview {
  client: { uid: string; name: string };
  consent: LegalConsentSettings;
  documents: Record<LegalDocumentType, LegalDocumentSlot>;
  // people = active people in the client (the "of M"); total = exactly who the
  // sign-out action would sign out; per-type counts are null when that checkbox is off.
  missing: { people: number; total: number; PRIVACY_TERMS: number | null; SMS: number | null };
}

export interface PublishLegalDocumentPayload {
  title: string;
  content: string;
  effectiveDate: string;
}

export interface ConsentState {
  status: 'ACCEPTED' | 'MISSING' | 'WITHDRAWN';
  at?: string | null;
  platform?: ConsentPlatform | null;
  source?: ConsentSource | null;
  privacyVersion?: number | null;
  termsVersion?: number | null;
  recordedBy?: { uid: string; name: string } | null;
}

export interface ConsentReportRow {
  uid: string;
  name: string;
  phone?: string | null;
  role?: string | null;
  lastLoginAt?: string | null;
  // null = that consent does not apply to this person (checkbox off, role can't sign in
  // where it's required, or no phone number for SMS). Rendered as "Not required".
  privacyTerms: ConsentState | null;
  sms: ConsentState | null;
}

export type ConsentReportTypeFilter = 'ALL' | ConsentType;
export type ConsentReportStatusFilter = 'ALL' | 'MISSING' | 'ACCEPTED';

export interface ConsentReportParams {
  type?: ConsentReportTypeFilter;
  status?: ConsentReportStatusFilter;
  search?: string;
  page?: number;
  // Server cap is 5000.
  limit?: number;
}

export interface ConsentReportPage {
  rows: ConsentReportRow[];
  total: number;
  page: number;
  limit: number;
}

export interface ConsentRecordView {
  uid: string;
  type: ConsentType;
  action: 'GRANTED' | 'WITHDRAWN';
  platform: ConsentPlatform | null;
  source: ConsentSource;
  documents: Array<{ type: LegalDocumentType; version: number }>;
  phoneNumber?: string | null;
  countryCode?: string | null;
  recordedBy: { uid: string; name: string } | null;
  evidenceNote?: string | null;
  createdAt: string;
}

export interface ResourceConsentDetails {
  privacyTermsEnabled: boolean;
  smsEnabled: boolean;
  current: { privacyTerms: ConsentState; sms: ConsentState };
  history: ConsentRecordView[];
}

export interface RecordSmsConsentPayload {
  action: 'GRANTED' | 'WITHDRAWN';
  evidenceNote: string;
}

// `consents` on every ResourceData from the resource endpoints. Either key may be
// absent, meaning nothing is on record.
export interface ResourceConsentsSummary {
  privacyTerms?: {
    granted: boolean;
    at?: string | null;
    privacyVersion?: number | null;
    termsVersion?: number | null;
    platform?: ConsentPlatform | null;
    source?: ConsentSource | null;
  } | null;
  sms?: {
    granted: boolean;
    at?: string | null;
    phoneNumber?: string | null;
    countryCode?: string | null;
    platform?: ConsentPlatform | null;
    source?: ConsentSource | null;
  } | null;
}

// Thrown by login() / microsoftLogin() on a 428 so the login page can show exactly the
// boxes the server asked for. Detected by shape (see isConsentRequiredError), not by
// instanceof, which is unreliable for Error subclasses compiled to ES5.
export class ConsentRequiredError extends Error {
  code = 'CONSENT_REQUIRED' as const;
  required: ConsentType[];
  documents: LegalDocumentsMeta;

  constructor(message: string, required: ConsentType[], documents: LegalDocumentsMeta) {
    super(message);
    Object.setPrototypeOf(this, ConsentRequiredError.prototype);
    this.name = 'ConsentRequiredError';
    this.required = required;
    this.documents = documents;
  }
}

export const isConsentRequiredError = (error: unknown): error is ConsentRequiredError =>
  Boolean(error) && (error as any).code === 'CONSENT_REQUIRED' && Array.isArray((error as any).required);

const CONSENT_TYPES: ConsentType[] = ['PRIVACY_TERMS', 'SMS'];

// Turns a raw 428 envelope into a ConsentRequiredError, or null for anything else.
export const consentRequiredFromResponse = (body: any): ConsentRequiredError | null => {
  if (!body || typeof body !== 'object') return null;
  const data = body.data && typeof body.data === 'object' ? body.data : {};
  const code = data.code ?? body.code;
  if (code !== 'CONSENT_REQUIRED' && body.statusCode !== 428) return null;

  const required = (Array.isArray(data.required) ? data.required : []).filter((t: any): t is ConsentType => CONSENT_TYPES.includes(t));
  const documents: LegalDocumentsMeta = {
    PRIVACY_POLICY: data.documents?.PRIVACY_POLICY ?? null,
    TERMS: data.documents?.TERMS ?? null
  };
  return new ConsentRequiredError(
    typeof body.message === 'string' && body.message ? body.message : CONSENT_COPY.consentRequired,
    required,
    documents
  );
};
